import { useEffect, useMemo, useState } from 'react'
import NavTable from '../table/NavTable'
import { MultiSelect } from '../inputs'
import { useTopics } from '../../hooks/useTopics'
import { listGlobalChats } from '../../services/globalChats'
import { globalChatLabel } from './globalChatLabel'
import { topicLabelFor } from '../../utils/topicLabel'

// The chat list in the Global Search nav column. A user opens a chat to load it
// into the active window (window title = chat label). `reloadSignal` (bumped
// after create/edit/delete) forces a refetch. A topic multiselect at the top
// filters the loaded chats by their tagged topics (client-side).
function GlobalChatList({ reloadSignal, selectedId, onSelect }) {
  const [chats, setChats] = useState([])
  const [status, setStatus] = useState('loading') // loading | ready | error
  const { topics: topicOptions } = useTopics()
  const [topicFilter, setTopicFilter] = useState([])

  useEffect(() => {
    let alive = true
    listGlobalChats()
      .then((list) => {
        if (!alive) return
        setChats(list)
        setStatus('ready')
      })
      .catch(() => alive && setStatus('error'))
    return () => {
      alive = false
    }
  }, [reloadSignal])

  const filtered = useMemo(() => {
    if (!topicFilter.length) return chats
    const want = new Set(topicFilter.map((t) => String(t._id)))
    return chats.filter((chat) =>
      (chat.topics || []).some((t) => want.has(String(t?._id ?? t)))
    )
  }, [chats, topicFilter])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Chat',
        render: (chat) => {
          const label = globalChatLabel(chat)
          return (
            <span className="nav-ellipsis" title={label}>
              {label}
            </span>
          )
        },
      },
    ],
    []
  )

  if (status === 'loading') return <div className="nav-table-status">Loading…</div>
  if (status === 'error') return <div className="nav-table-status">Could not load chats.</div>

  return (
    <>
      {topicOptions.length ? (
        <div className="nav-topic-filter">
          <MultiSelect
            value={topicFilter}
            onChange={setTopicFilter}
            options={topicOptions}
            getOptionValue={(t) => t._id}
            getOptionLabel={topicLabelFor(topicOptions)}
            placeholder="Filter by topic…"
            clearQueryOnSelect={false}
          />
        </div>
      ) : null}
      <NavTable
        columns={columns}
        rows={filtered}
        getRowId={(chat) => chat._id}
        selectedId={selectedId}
        onRowClick={onSelect}
        showHeader={false}
        emptyLabel={
          topicFilter.length ? 'No chats match these topics.' : 'No chats yet. Create one to get started.'
        }
      />
    </>
  )
}

export default GlobalChatList
